import { AfterViewChecked, AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Renderer2, RendererStyleFlags2, inject } from '@angular/core';

/**
 * 검색 조건 옵션 접기: 검색 박스(.np-form-table)에서 np-search-more 로 지정한 줄을 접어 두고, 펼치면 그리드 위에 겹쳐 띄운다.
 * - selector 가 .np-form-table 이므로 이 디렉티브를 import 한 컴포넌트에 자동으로 적용된다. (NpxCommonModule)
 *   조회/초기화(.np-search-actions)와 np-search-more 줄이 있는 화면의 검색 박스에서만 동작한다. (모달 안은 제외)
 * - 조회/초기화 아래에 '옵션 전체 / 옵션 접기' 버튼을 만들고, 접힌 줄에 조건이 걸려 있으면 버튼에 그 항목 수를 배지로 표시한다.
 * - 펼쳐도 박스가 차지하는 높이는 접힌 높이 그대로라서 그리드 크기가 바뀌지 않는다. (스타일: nova-common-styles.scss 의 np-search-overlay)
 * - 접힌 줄의 조건도 검색에는 그대로 적용된다.
 * - 펼친 상태는 조회(조회 버튼, 입력칸 Enter), 바깥 클릭, 창 크기 변경 시 닫힌다.
 * - 검색 항목 열 맞춤(조회/초기화가 있는 박스, np-search-compact / np-search-flow 제외): 넓은 항목은 내용이 접히지 않을 만큼 칸을 늘리고,
 *   줄 끝에 빈 칸이 남으면 그 줄 마지막 항목을 줄 끝까지 늘린다.
 * 사용:
 *   <div class="np-form-table">
 *     <div class="row g-0"> 항상 보이는 항목 </div>
 *     <div class="row g-0 np-search-more"> 접는 항목 </div>
 *     <div class="np-search-actions"> 조회 / 초기화 </div>
 *   </div>
 */
@Directive({
    selector: '.np-form-table',
    standalone: true
})
export class NpSearchOverlayDirective implements AfterViewInit, AfterViewChecked, OnDestroy {

    private el: ElementRef<HTMLElement> = inject(ElementRef);
    private renderer = inject(Renderer2);

    private active = false;
    private open = false;

    private toggleButton?: HTMLButtonElement;
    private appliedBadge?: HTMLElement;
    private appliedCount = -1;
    private unlisteners: (() => void)[] = [];

    private gridActive = false;
    private gridWidth = 0;
    private gridFrame = 0;
    private gridObservers: (ResizeObserver | MutationObserver)[] = [];

    ngAfterViewInit() {
        const box = this.el.nativeElement;
        this.initGrid(box);

        const actions = box.querySelector<HTMLElement>(':scope > .np-search-actions');
        this.active = !!actions && this.hiddenItems().length > 0 && !box.closest('.modal');
        if (!this.active) return;

        this.renderer.addClass(box, 'np-search-overlay');
        this.createToggleButton(actions!);

        // 문서 / 창 이벤트는 동작하는 박스에만 단다. (.np-form-table 은 입력 폼에도 많이 쓰임)
        this.unlisteners.push(
            this.renderer.listen('document', 'mousedown', (event: MouseEvent) => this.onDocumentMouseDown(event)),
            // 창 크기가 바뀌면 줄바꿈이 달라져 겹치는 높이가 맞지 않으므로 닫는다.
            this.renderer.listen('window', 'resize', () => this.setOpen(false))
        );
    }

    // 접힌 조건이 바뀌면(입력, 체크, 초기화 등) 배지를 다시 그린다. 하위 컴포넌트(체크박스 등)까지 그려진 뒤라 화면 값이 최신이다.
    ngAfterViewChecked() {
        if (!this.active) return;
        this.updateAppliedBadge();
    }

    private createToggleButton(actions: HTMLElement) {
        const button: HTMLButtonElement = this.renderer.createElement('button');
        this.renderer.setAttribute(button, 'type', 'button');
        this.renderer.setAttribute(button, 'class', 'btn btn-sm btn-outline-secondary list-action-btn position-relative');
        this.renderer.listen(button, 'click', () => this.setOpen(!this.open));
        this.renderer.appendChild(actions, button);
        this.toggleButton = button;
        this.updateToggleButton();
    }

    private updateToggleButton() {
        const button = this.toggleButton!;
        button.innerHTML = this.open
            ? '<i class="fa-solid fa-chevron-up"></i> 옵션 접기'
            : '<i class="fa-solid fa-chevron-down"></i> 옵션 전체';
        this.appliedBadge = undefined;
        this.appliedCount = -1;
        this.updateAppliedBadge();
    }

    // 접힌 항목 중 조건이 걸린 항목 수를 버튼 오른쪽 위 배지로 표시한다. (펼쳤을 때는 표시하지 않음)
    private updateAppliedBadge() {
        const count = this.open ? 0 : this.hiddenItems().filter(item => this.isApplied(item)).length;
        if (count === this.appliedCount) return;
        this.appliedCount = count;

        if (!count) {
            this.appliedBadge?.remove();
            this.appliedBadge = undefined;
            return;
        }
        if (!this.appliedBadge) {
            this.appliedBadge = this.renderer.createElement('span');
            this.renderer.addClass(this.appliedBadge, 'np-search-applied');
            this.renderer.appendChild(this.toggleButton, this.appliedBadge);
        }
        this.appliedBadge!.textContent = String(count);
        this.renderer.setAttribute(this.appliedBadge, 'title', `접힌 조건 중 ${count}개 적용 중`);
    }

    private hiddenItems() {
        return Array.from(this.el.nativeElement.querySelectorAll<HTMLElement>(':scope > .row.np-search-more > .np-col-set'));
    }

    // 조건이 걸린 항목: 화면 값(DOM)으로 판단한다. 입력칸에 값이 있거나, 체크박스를 체크했거나, select / 라디오에서 첫 번째(전체)가 아닌 것을 골랐을 때
    private isApplied(item: HTMLElement) {
        const radios = Array.from(item.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
        if (radios.some((radio, i) => radio.checked && i > 0)) return true;

        return Array.from(item.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input:not([type="radio"]), select')).some(input => {
            if (input instanceof HTMLSelectElement) return input.selectedIndex > 0;
            if (input.type === 'checkbox') return input.checked;
            return input.value.trim() !== '';
        });
    }

    private setOpen(open: boolean) {
        if (!this.active || this.open === open) return;
        const box = this.el.nativeElement;

        if (open) {
            // 펼친 만큼(접힌 줄 높이) 아래 여백을 줄여 그리드 위로 겹치게 한다.
            const collapsedHeight = box.offsetHeight;
            this.renderer.addClass(box, 'np-search-overlay-open');
            this.layoutGrid();
            const moreHeight = box.offsetHeight - collapsedHeight;
            this.renderer.setStyle(box, '--np-search-more-height', `${moreHeight}px`, RendererStyleFlags2.DashCase);
        } else {
            this.renderer.removeClass(box, 'np-search-overlay-open');
            this.renderer.removeStyle(box, '--np-search-more-height', RendererStyleFlags2.DashCase);
            this.layoutGrid();
        }
        this.open = open;
        this.updateToggleButton();
    }

    // 검색 항목 열 맞춤: 박스 폭이 바뀌거나 항목이 바뀌면(항목 추가 / 삭제) 다시 맞춘다. (스타일: nova-common-styles.scss 의 검색 항목 열 맞춤)
    private initGrid(box: HTMLElement) {
        this.gridActive = !!box.querySelector(':scope > .np-search-actions') && !!box.querySelector('.np-col-set')
            && !box.classList.contains('np-search-compact') && !box.classList.contains('np-search-flow');
        if (!this.gridActive) return;

        const resizeObserver = new ResizeObserver(() => {
            // 높이만 바뀐 경우(칸을 늘려 줄 수가 바뀜)는 다시 맞추지 않는다.
            if (box.clientWidth === this.gridWidth) return;
            this.scheduleGridLayout();
        });
        const mutationObserver = new MutationObserver(() => this.scheduleGridLayout());
        resizeObserver.observe(box);
        mutationObserver.observe(box, { childList: true, subtree: true });
        this.gridObservers.push(resizeObserver, mutationObserver);
    }

    private scheduleGridLayout() {
        cancelAnimationFrame(this.gridFrame);
        this.gridFrame = requestAnimationFrame(() => this.layoutGrid());
    }

    private layoutGrid() {
        if (!this.gridActive) return;
        const box = this.el.nativeElement;
        this.gridWidth = box.clientWidth;

        // 보이는 항목만 맞춘다. (옵션을 접으면 접힌 줄은 빠진다)
        const items = Array.from(box.querySelectorAll<HTMLElement>(':scope > .row > .np-col-set'))
            .filter(item => item.getClientRects().length > 0);
        items.forEach(item => this.setGridSpan(item, 1));

        // 1. 넓은 항목: 내용을 한 줄로 펼친 폭(입력칸은 최소 폭)이 한 칸보다 넓으면 그 폭이 들어가는 만큼 칸을 늘린다.
        //    잴 때만 항목을 내용 폭으로 두고(스타일: np-search-grid-measure), 폭을 정하지 않은 입력칸은 0 으로 줄여 최소 폭(min-width)만 잰다.
        //    화면에서 폭을 정한 입력칸(날짜 등)은 그 폭 그대로 잰다.
        const style = getComputedStyle(box);
        const columns = style.gridTemplateColumns.split(' ').length;
        const columnWidth = (box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)) / columns;
        const flexibleInputs = Array.from(box.querySelectorAll<HTMLElement>('.np-col-cell > .form-control, .np-col-cell > .form-select'))
            .filter(input => !input.style.width);
        this.renderer.addClass(box, 'np-search-grid-measure');
        flexibleInputs.forEach(input => this.renderer.setStyle(input, 'width', '0'));
        const spans = items.map(item => Math.min(Math.ceil(item.offsetWidth / columnWidth - 0.01), columns));
        flexibleInputs.forEach(input => this.renderer.removeStyle(input, 'width'));
        this.renderer.removeClass(box, 'np-search-grid-measure');
        items.forEach((item, i) => this.setGridSpan(item, spans[i]));

        // 2. 줄 끝 빈 칸: 줄마다 마지막(오른쪽 끝) 항목을 줄 끝까지 늘린다.
        //    항목은 순서대로 놓여 빈 칸은 줄 끝에만 생기므로(넓은 항목이 안 들어가 다음 줄로 넘어감) 늘려도 다른 항목 자리는 바뀌지 않는다.
        const lines = this.gridColumnLines(box);
        const rowLastItems = new Map<number, HTMLElement>();
        items.forEach(item => {
            const top = Math.round(item.getBoundingClientRect().top);
            const last = rowLastItems.get(top);
            if (!last || item.getBoundingClientRect().left > last.getBoundingClientRect().left) {
                rowLastItems.set(top, item);
            }
        });
        rowLastItems.forEach(item => {
            const rect = item.getBoundingClientRect();
            const start = this.nearestLine(lines, rect.left);
            const end = this.nearestLine(lines, rect.right);
            if (end < lines.length - 1) {
                this.setGridSpan(item, lines.length - 1 - start);
            }
        });
    }

    private setGridSpan(item: HTMLElement, span: number) {
        if (span > 1) {
            this.renderer.setStyle(item, 'grid-column', `span ${span}`, RendererStyleFlags2.DashCase);
        } else {
            this.renderer.removeStyle(item, 'grid-column', RendererStyleFlags2.DashCase);
        }
    }

    // 열 경계선의 x 좌표 (열 사이 간격은 없다). auto-fit 으로 비어서 접힌 열(0px)은 뺀다.
    private gridColumnLines(box: HTMLElement) {
        const style = getComputedStyle(box);
        const widths = style.gridTemplateColumns.split(' ').map(parseFloat).filter(width => width > 0);
        let x = box.getBoundingClientRect().left + box.clientLeft + parseFloat(style.paddingLeft);
        const lines = [x];
        widths.forEach(width => lines.push(x += width));
        return lines;
    }

    private nearestLine(lines: number[], x: number) {
        let nearest = 0;
        lines.forEach((line, i) => {
            if (Math.abs(line - x) < Math.abs(lines[nearest] - x)) nearest = i;
        });
        return nearest;
    }

    // 조회 버튼을 누르면 닫는다.
    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        if ((event.target as HTMLElement).closest('.np-search-actions > .btn-primary')) {
            this.setOpen(false);
        }
    }

    // 입력칸에서 Enter 로 조회하면 닫는다. (select 를 바꿔 바로 조회되는 경우는 여러 항목을 이어서 고를 수 있게 닫지 않는다)
    @HostListener('keyup.enter', ['$event'])
    onEnter(event: Event) {
        if ((event.target as HTMLElement).tagName === 'INPUT') {
            this.setOpen(false);
        }
    }

    // 바깥을 누르면 닫는다. body 에 붙는 팝업(날짜 선택, 드롭다운 목록 등)은 박스 밖이지만 박스의 일부로 본다.
    private onDocumentMouseDown(event: MouseEvent) {
        if (!this.open) return;
        const target = event.target as HTMLElement;
        if (this.el.nativeElement.contains(target)) return;
        if (target.closest('.dropdown-menu, ngb-datepicker, .ng-dropdown-panel, .wj-dropdown-panel, .cdk-overlay-container, .modal')) return;
        this.setOpen(false);
    }

    ngOnDestroy() {
        this.unlisteners.forEach(unlisten => unlisten());
        this.gridObservers.forEach(observer => observer.disconnect());
        cancelAnimationFrame(this.gridFrame);
    }
}
